import {
  FETCH_ECTS_LOADING,
  FETCH_ECTS_SUCCESS,
  FETCH_ECTS_ERROR,
  FETCH_ECT_LOADING,
  FETCH_ECT_SUCCESS,
  FETCH_ECT_ERROR,
  CREATE_ECT_LOADING,
  CREATE_ECT_SUCCESS,
  CREATE_ECT_ERROR,
  UPDATE_ECT_LOADING,
  UPDATE_ECT_SUCCESS,
  UPDATE_ECT_ERROR,
  DELETE_ECT_LOADING,
  DELETE_ECT_SUCCESS,
  DELETE_ECT_ERROR,
  ACTIVATE_INVALID_BOT_DIALOGUE,
  DEACTIVATE_INVALID_BOT_DIALOGUE,
  ACTIVATE_CONTACT_SALES_TEAM_DIALOGUE,
  DEACTIVATE_CONTACT_SALES_TEAM_DIALOGUE,
} from './actionTypes';

import { ECT_CREDENTIALS, ZOHO_FQDN } from '../const';

const initialState = {
  isActiveToolExist: false,
  isActiveToolConfigured: false,
  isInvalidBotDialogueActive: false,
  contactSalesTeamDialogToolName: '',
  isContactSalesTeamDialogActive: false,
  activeToolData: {},
  toolList: [],
  fields: [],
  videoID: 'VSB4wGIdDwo',
  contactUsDocUrl: 'https://docs.haptik.ai/contact-us',
  ectsFetched: false,
  ectCredentials: ECT_CREDENTIALS,
  loading: false,
  error: false,
};

const zohoHiddenFields = [
  'app_id',
  'bot_id',
  'department_ids',
  'screen_name',
  'undefined',
  'refresh_token',
];

const getFields = (state, action) => {
  const isZoho = action.payload.configuration.ect_fqdn === ZOHO_FQDN;
  const indexedFields = state.ectCredentials[
    action.payload.configuration.ect_fqdn
  ].credentials.reduce((acc, field) => {
    return {
      ...acc,
      [field.name]: { ...field },
    };
  }, {});

  Object.keys(action.payload?.configuration?.credentials ?? {}).forEach(
    (name) => {
      const value = action.payload.configuration.credentials[name];
      if (indexedFields[name])
        indexedFields[name] = {
          ...indexedFields[name],
          value,
          hiddenField: false,
        };
      else
        indexedFields[name] = {
          key: name,
          required: false,
          type: 'TEXT',
          value,
          hiddenField: isZoho && zohoHiddenFields.includes(name),
        };
    }
  );

  const fields = Object.keys(indexedFields).map((key) => indexedFields[key]);
  return fields;
};

const swapPositions = (array, from, to) => {
  [array[from], array[to]] = [array[to], array[from]];
  return array;
};

const reducer = (state, action) => {
  switch (action.type) {
    case FETCH_ECTS_LOADING:
      return { ...state, loading: true };
    case FETCH_ECTS_SUCCESS: {
      // tool_type is 0 for Humand and 1 for bot
      const metadata = action.payload.metadata.filter(
        (tool) => !tool.tool_type
      );
      const activeTool = metadata.filter(
        (toolData) =>
          toolData.enabled &&
          toolData.fqdn !== 'athena' &&
          !toolData.fqdn.includes('buzzo')
      );
      const ectCredentials = metadata.reduce(
        (acc, curr) => {
          if (state.ectCredentials[curr.fqdn])
            return {
              ...acc,
              [curr.fqdn]: {
                ...(acc[curr.fqdn] || { credentials: [] }),
                iconUrl: curr.url_icon,
                toolDocUrl: curr.url_documentation,
                lastEdited: curr.modified_at,
                name: curr.human_name,
                callbackURL: curr.callback_url,
              },
            };
          return acc;
        },
        { ...state.ectCredentials }
      );

      let activeToolData = {};
      const isActiveToolExist = activeTool.length > 0;
      const filteredToolList = metadata.filter(
        (toolData) => !toolData.enabled || toolData.fqdn === 'athena'
      );
      const athenaToolIndex = filteredToolList.findIndex(
        (tools) => tools.fqdn === 'athena'
      );
      const updatedToollist = swapPositions(
        filteredToolList,
        athenaToolIndex,
        0
      );

      const toolList = updatedToollist.map((tool) => ({
        iconUrl: tool.url_icon,
        toolDocUrl: tool.url_documentation,
        lastEdited: tool.modified_at,
        name: tool.human_name,
        enabled: tool.enabled,
        fqdn: tool.fqdn,
        toolType: tool.tool_type,
      }));

      if (isActiveToolExist) {
        activeToolData = {
          iconUrl: activeTool[0].url_icon,
          toolDocUrl: activeTool[0].url_documentation,
          lastEdited: activeTool[0].modified_at,
          name: activeTool[0].human_name,
          enabled: activeTool[0].enabled,
          fqdn: activeTool[0].fqdn,
          toolType: activeTool[0].tool_type,
        };
      }

      return {
        ...state,
        isActiveToolExist,
        activeToolData,
        toolList,
        ectCredentials,
        loading: false,
        ectsFetched: true,
      };
    }
    case FETCH_ECTS_ERROR:
      return { ...state, error: true, loading: false };

    case FETCH_ECT_LOADING:
      return {
        ...state,
        isActiveToolConfigured: false,
        loading: true,
      };

    case FETCH_ECT_SUCCESS: {
      const fqdn = action?.payload?.configuration?.ect_fqdn;
      const fields = getFields(state, action);
      const isActiveToolConfigured = Boolean(
        action.payload?.configuration?.credentials
      );
      const {
        configuration,
        message_delivery_url: callbackUrlDomain,
        zohoAuthResponse = {},
      } = action?.payload;
      let callbackURL = configuration?.callback_url || '';
      const testConfigData = localStorage.getItem('testConfigData');
      if (testConfigData && fqdn === ZOHO_FQDN) {
        const businessId = JSON.parse(testConfigData)?.business_id || '';
        callbackURL = `${configuration?.callback_url}/${businessId}`;
      }
      const updatedCallBackUrlDomain = new URL(callbackUrlDomain);
      updatedCallBackUrlDomain.port = '';
      const ectCredentials = {
        ...state.ectCredentials,
        [fqdn]: {
          ...state.ectCredentials?.[fqdn],
          callbackURL,
          callbackUrlDomain: updatedCallBackUrlDomain.toString(),
          zohoAuthResponse,
        },
      };

      return {
        ...state,
        fields,
        ectCredentials,
        isActiveToolConfigured,
        loading: false,
      };
    }

    case FETCH_ECT_ERROR: {
      return {
        ...state,
        isActiveToolConfigured: false,
        loading: false,
      };
    }

    // Optimistic UI
    case CREATE_ECT_LOADING: {
      const fields = getFields(state, action);
      return {
        ...state,
        fields,
        isActiveToolExist: true,
        isActiveToolConfigured: true,
      };
    }

    case CREATE_ECT_SUCCESS: {
      const fqdn = action.payload.configuration.ect_fqdn;
      const toolList = state.toolList.filter((tool) => tool.fqdn !== fqdn);
      const activeTool = state.toolList.filter((tool) => tool.fqdn === fqdn);
      const activeToolData = {
        ...activeTool[0],
      };

      return {
        ...state,
        toolList,
        activeToolData,
      };
    }

    case CREATE_ECT_ERROR: {
      const fields = getFields(state, action);
      return {
        ...state,
        fields,
        isActiveToolExist: false,
        isActiveToolConfigured: false,
      };
    }

    // Optimistic UI
    case UPDATE_ECT_LOADING: {
      const fields = getFields(state, action);
      return {
        ...state,
        fields,
      };
    }
    case UPDATE_ECT_SUCCESS:
      return state;
    case UPDATE_ECT_ERROR: {
      const fields = getFields(state, action);
      return {
        ...state,
        fields,
      };
    }
    case DELETE_ECT_LOADING:
      return {
        ...state,
        isActiveToolExist: false,
      };
    case DELETE_ECT_SUCCESS:
      return {
        ...state,
        toolList: [{ ...state.activeToolData }, ...state.toolList],
        activeToolData: {},
        isActiveToolConfigured: false,
        isActiveToolExist: false,
      };
    case DELETE_ECT_ERROR:
      return {
        ...state,
        isActiveToolExist: true,
      };

    case ACTIVATE_INVALID_BOT_DIALOGUE:
      return {
        ...state,
        isInvalidBotDialogueActive: true,
      };

    case DEACTIVATE_INVALID_BOT_DIALOGUE:
      return {
        ...state,
        isInvalidBotDialogueActive: false,
      };

    case ACTIVATE_CONTACT_SALES_TEAM_DIALOGUE:
      return {
        ...state,
        contactSalesTeamDialogToolName: action.payload.toolName,
        isContactSalesTeamDialogActive: true,
      };

    case DEACTIVATE_CONTACT_SALES_TEAM_DIALOGUE:
      return {
        ...state,
        isContactSalesTeamDialogActive: false,
      };

    default:
      return state;
  }
};

const init = (initialState) => ({ ...initialState });

export { initialState, init };

export default reducer;
